import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Trash2, AlertTriangle, BookOpen, FileText, Video, Volume2, Image as ImageIcon, Code, ClipboardCheck } from "lucide-react";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";

interface SectionInfo {
  id: string;
  title: string;
  description: string;
  contents?: Array<any>;
  createdAt: string;
  updatedAt: string;
}

interface LessonInfo {
  id: string;
  title: string;
  description: string;
}

export default function DeleteSectionPage() {
  const navigate = useNavigate();
  const { lessonId, sectionId } = useParams<{
    lessonId: string;
    sectionId: string;
  }>();
  const hasDeletePermission = checkPermission("lesson_update");

  useEffect(() => {
    if (!hasDeletePermission) {
      navigate("/lessons");
    }
  }, [hasDeletePermission, navigate]);

  const [section, setSection] = useState<SectionInfo | null>(null);
  const [lesson, setLesson] = useState<LessonInfo | null>(null);
  const [contentCount, setContentCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!lessonId || !sectionId) return;
      try {
        setLoading(true);
        setError("");

        const [lessonData, sectionsData] = await Promise.all([ApiService.getLesson(lessonId), ApiService.getLessonSections(lessonId)]);

        setLesson(lessonData);

        const sections = Array.isArray(sectionsData) ? sectionsData : sectionsData?.data || [];
        const currentSection = sections.find((s: any) => s.id === sectionId);

        if (!currentSection) {
          setError("Bölüm bulunamadı");
          return;
        }

        try {
          const contents = await ApiService.getSectionContents(lessonId, sectionId);
          const sectionWithContents = { ...currentSection, contents };
          setSection(sectionWithContents);
          setContentCount(contents?.length || 0);
        } catch (err) {
          setSection(currentSection);
          setContentCount(0);
        }
      } catch (err: any) {
        setError(err.message || "Bölüm bilgileri yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [lessonId, sectionId]);

  const handleDelete = async () => {
    if (!lessonId || !sectionId || !section) return;
    if (confirmText !== section.title) {
      setError("Bölüm adını doğru yazmadınız!");
      return;
    }
    try {
      setDeleting(true);
      setError("");
      await ApiService.deleteLessonSection(lessonId, sectionId);
      navigate(`/lessons/${lessonId}`);
    } catch (err: any) {
      setError(err.message || "Bölüm silinemedi");
    } finally {
      setDeleting(false);
    }
  };

  // İçerik türü ikonları
  const getContentTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return <Video className="w-4 h-4" />;
      case "AUDIO":
        return <Volume2 className="w-4 h-4" />;
      case "IMAGE":
        return <ImageIcon className="w-4 h-4" />;
      case "TEXT":
        return <FileText className="w-4 h-4" />;
      case "FILE":
      case "PDF":
        return <FileText className="w-4 h-4" />;
      case "CODE":
        return <Code className="w-4 h-4" />;
      case "TEST":
      case "EXAM":
        return <ClipboardCheck className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getContentTypeName = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return "Video";
      case "AUDIO":
        return "Ses";
      case "IMAGE":
        return "Resim";
      case "TEXT":
        return "Metin";
      case "FILE":
        return "Dosya";
      case "PDF":
        return "PDF";
      case "CODE":
        return "Kod";
      case "TEST":
      case "EXAM":
        return "Test";
      default:
        return "İçerik";
    }
  };

  const getContentTypeBadgeClass = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return "bg-purple-100 text-purple-700";
      case "AUDIO":
        return "bg-blue-100 text-blue-700";
      case "IMAGE":
        return "bg-green-100 text-green-700";
      case "TEXT":
        return "bg-gray-100 text-gray-700";
      case "FILE":
      case "PDF":
        return "bg-red-100 text-red-700";
      case "CODE":
        return "bg-yellow-100 text-yellow-700";
      case "TEST":
      case "EXAM":
        return "bg-emerald-100 text-emerald-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  if (!hasDeletePermission) {
    return null;
  }

  const hasContents = contentCount > 0;

  return (
    <div className="h-[88vh] bg-gray-50 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">E-Eğitim</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button
                onClick={() => navigate("/lessons")}
                className={`pb-3 transition-all duration-200 text-blue-600 font-medium border-b-2 border-blue-500`}>
                Dersler
              </button>
              <button onClick={() => navigate("/lessons?tab=homeworks")} className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                Ödevler
              </button>
              <button
                onClick={() => navigate("/lessons?tab=deficiencies")}
                className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                Eksikler
              </button>
              <button onClick={() => navigate("/lessons?tab=reports")} className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                Raporlar
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Bölümü Sil</h1>
                <p className="text-red-100 text-sm mt-1">Bu işlem geri alınamaz. Lütfen dikkatli olun.</p>
              </div>
              {section && (
                <div className="text-right">
                  <p className="text-white font-medium text-lg">{section.title}</p>
                  <p className="text-red-100 text-sm">Silinecek Bölüm</p>
                </div>
              )}
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mb-6">{error}</div>}

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : section ? (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <h3 className="text-red-800 font-semibold">Dikkat!</h3>
                </div>
                <p className="text-red-700 text-sm mb-3">
                  Bu işlem geri alınamaz. Bölümü sildikten sonra aşağıdaki veriler kalıcı olarak kaybolacaktır:
                </p>
                <ul className="space-y-3 text-red-700 text-sm">
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Bölüm Bilgileri ve Ayarları</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Bölüm İçindeki Tüm İçerikler</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Öğrenci İlerlemeleri</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">İlişkili Ödevler ve Testler</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                    <span className="font-medium">Yorumlar ve Notlar</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <BookOpen className="w-5 h-5 text-red-600" />
                  <h3 className="text-gray-900 font-medium">Bölüm Bilgileri</h3>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Bölüm Adı</label>
                      <p className="text-gray-900 font-medium">{section.title}</p>
                    </div>
                    {section.description && (
                      <div>
                        <label className="block text-gray-500 text-xs mb-1">Açıklama</label>
                        <p className="text-gray-700 text-sm">{section.description}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-red-600" />
                        <span className="text-red-700 text-sm font-medium">İçerik Sayısı</span>
                      </div>
                      <p className={`text-2xl font-bold ${hasContents ? "text-red-600" : "text-red-400"}`}>{contentCount}</p>
                    </div>

                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-red-600" />
                        <span className="text-red-700 text-sm font-medium">Bağlı Ders</span>
                      </div>
                      <p className="text-red-600 font-medium">{lesson?.title}</p>
                    </div>
                  </div>

                  {/* İçerik Listesi */}
                  {section.contents && section.contents.length > 0 && (
                    <div>
                      <label className="block text-gray-500 text-xs mb-2">Silinecek İçerikler ({section.contents.length} adet)</label>
                      <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                        {section.contents.map((content: any, index: number) => (
                          <div key={content.id || index} className="flex items-center gap-3 p-2 bg-white rounded-md border border-gray-100">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${getContentTypeBadgeClass(content.type)}`}>
                              {getContentTypeIcon(content.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{content.title || `İçerik ${index + 1}`}</p>
                              <p className="text-xs text-gray-500">{getContentTypeName(content.type)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Oluşturma Tarihi</label>
                      <p className="text-gray-700 text-sm">{new Date(section.createdAt).toLocaleDateString("tr-TR")}</p>
                    </div>
                    <div>
                      <label className="block text-gray-500 text-xs mb-1">Son Güncelleme</label>
                      <p className="text-gray-700 text-sm">{new Date(section.updatedAt).toLocaleDateString("tr-TR")}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Trash2 className="w-5 h-5 text-red-600" />
                  <h4 className="text-gray-900 font-medium">Silme İşlemini Onayla</h4>
                </div>
                <p className="text-gray-600 text-sm mb-4">Silme işlemini onaylamak için bölüm adını tam olarak yazın:</p>
                <div className="mb-4">
                  <p className="text-red-700 font-mono text-sm mb-2 bg-red-50 p-2 rounded border">{section.title}</p>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Bölüm adını buraya yazın..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  />
                </div>
                {confirmText && confirmText !== section.title && (
                  <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 p-3 rounded-lg border border-red-200">
                    <span className="text-red-500 text-sm">❌</span>
                    <span className="font-medium">Girilen metin bölüm adı ile eşleşmiyor.</span>
                  </div>
                )}
                {confirmText === section.title && (
                  <div className="flex items-center gap-2 text-green-600 text-xs bg-green-50 p-3 rounded-lg border border-green-200">
                    <span className="text-green-500 text-sm">✅</span>
                    <span className="font-medium">Bölüm adı doğru şekilde girildi.</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate(-1)}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl border hover:bg-gray-50 text-sm font-medium disabled:opacity-50">
                  İptal
                </button>
                <button
                  onClick={handleDelete}
                  disabled={confirmText !== section.title || deleting || loading}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-60 text-sm font-medium flex items-center justify-center gap-2">
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Siliniyor...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Bölümü Sil
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-center py-16 text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">Bölüm bilgileri yüklenemedi</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
